import React, { ErrorInfo, ReactNode } from 'react';

interface Props { children: ReactNode; }
interface State { hasError: boolean; recoverable: boolean; }

// Erreurs transitoires où UN rechargement récupère l'app :
//  - chunk lazy périmé après un déploiement (Vite code-split) :
//      "Failed to fetch dynamically imported module" / "Loading chunk failed"
//  - DOM muté par une extension / la traduction auto du navigateur (Google Traduction) →
//      la réconciliation React jette
//      "Failed to execute 'insertBefore'|'removeChild' on 'Node'" (NotFoundError)
const RECOVERABLE_PATTERNS = [
  'failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'loading chunk',
  'loading css chunk',
  "failed to execute 'insertbefore'",
  "failed to execute 'removechild'",
  'notfounderror',
];

// Horodatage du dernier rechargement auto (sessionStorage) : garde anti-boucle.
const RELOAD_TS_KEY = 'tc-root-err-reload-ts';
const RELOAD_COOLDOWN_MS = 10_000;

function isRecoverable(error: Error): boolean {
  const msg = `${error?.name ?? ''} ${error?.message ?? ''}`.toLowerCase();
  return RECOVERABLE_PATTERNS.some(p => msg.includes(p));
}

/**
 * Boundary racine : convertit tout crash de rendu en écran lisible (jamais de page blanche).
 * Pour les erreurs transitoires connues, tente UN rechargement automatique (anti-boucle 10 s).
 * Volontairement distinct de <ErrorBoundary> (boîte rouge de debug) pour ne rien changer à
 * ses usages existants (WriterBot, genialeads…).
 */
export class RootErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, recoverable: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, recoverable: isRecoverable(error) };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[RootErrorBoundary]', error, info);

    if (!isRecoverable(error)) return;

    // Rechargement auto une seule fois par fenêtre de cooldown → jamais de boucle.
    let last = 0;
    try { last = Number(sessionStorage.getItem(RELOAD_TS_KEY)) || 0; } catch { /* ignore */ }
    const now = Date.now();
    if (now - last > RELOAD_COOLDOWN_MS) {
      try { sessionStorage.setItem(RELOAD_TS_KEY, String(now)); } catch { /* ignore */ }
      window.location.reload();
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        role="alert"
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '16px',
          padding: '24px',
          textAlign: 'center',
          fontFamily: 'Inter, system-ui, sans-serif',
          color: '#1f2937',
          background: '#f9fafb',
        }}
      >
        <div style={{ fontSize: '40px' }}>⚠️</div>
        <h1 style={{ fontSize: '20px', fontWeight: 700, margin: 0 }}>
          Une erreur d'affichage s'est produite
        </h1>
        <p style={{ maxWidth: '440px', fontSize: '14px', lineHeight: 1.5, margin: 0, color: '#4b5563' }}>
          Si votre navigateur traduit automatiquement cette page (Google Traduction),
          désactivez la traduction pour ce site, puis rechargez.
          Utilisez le sélecteur de langue dans l'application.
        </p>
        <button
          onClick={() => window.location.reload()}
          style={{
            marginTop: '4px',
            padding: '10px 20px',
            fontSize: '14px',
            fontWeight: 600,
            color: '#fff',
            background: '#E91E8C',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
          }}
        >
          Recharger la page
        </button>
      </div>
    );
  }
}
